import React, { useState, useMemo, useCallback } from 'react';
import { Button, Modal, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import { StepProps, StepsProps } from 'antd/lib/steps';
import { ModalProps } from 'antd/lib/modal';

type Step = {
  stepConfig: StepProps & { title: any };
  stepContent: React.ReactElement;
  stepDisableNext?: boolean;
};

interface IWizardProps {
  visibleState: any;
  stepsConfig: StepsProps;
  introStep?: React.ReactNode;
  arrSteps: Array<Step & { onBackStep?: Function; onNextStep?: Function }>;
  modalConfig?: ModalProps;
  onFinish?: Function;
  clearStates?: Function;
  hideSteps?: boolean;
}

const Wizard = ({
  visibleState,
  modalConfig,
  stepsConfig,
  introStep,
  arrSteps,
  onFinish,
  clearStates,
  hideSteps,
}: IWizardProps) => {
  const { t: translate } = useTranslation('wizard');

  const { Step } = Steps;
  const [currentStepNumber, setCurrentStepNumber] = useState(0);

  const [modalVisible, setModalVisible] = visibleState;
  const [finishButtonLoading, setFinishButtonLoading] = useState(false);

  const onCancel = useCallback(
    (e: any) => {
      if (modalConfig?.onCancel) modalConfig.onCancel(e);
      if (clearStates) clearStates();
      setModalVisible(false);
    },
    [modalConfig, clearStates, setModalVisible],
  );

  const handleOnFinish = useCallback(async () => {
    if (onFinish) {
      setFinishButtonLoading(true);
      await onFinish();
      setFinishButtonLoading(false);
      if (clearStates) clearStates();
    }
    setModalVisible(false);
  }, [clearStates, onFinish, setModalVisible]);

  const prevButton = useMemo(() => {
    return (
      currentStepNumber > 0 && (
        <Button
          className="button-previous"
          onClick={() => {
            if (currentStepNumber > 0)
              setCurrentStepNumber(currentStepNumber - 1);
            const currentstep = arrSteps[currentStepNumber];
            if (currentstep.onBackStep) {
              currentstep.onBackStep();
            }
          }}
        >
          {translate('buttons.previous')}
        </Button>
      )
    );
  }, [arrSteps, currentStepNumber, translate]);

  const nextButton = useMemo(() => {
    return (
      <>
        {currentStepNumber < arrSteps.length - 1 && (
          <Button
            type="primary"
            onClick={() => {
              if (currentStepNumber < arrSteps.length - 1)
                setCurrentStepNumber(currentStepNumber + 1);
              const currentstep = arrSteps[currentStepNumber];
              if (currentstep.onNextStep) {
                currentstep.onNextStep();
              }
            }}
            disabled={arrSteps[currentStepNumber].stepDisableNext}
          >
            {translate('buttons.next')}
          </Button>
        )}
      </>
    );
  }, [arrSteps, currentStepNumber, translate]);

  const finishButton = useMemo(() => {
    return (
      currentStepNumber === arrSteps.length - 1 && (
        <Button
          loading={finishButtonLoading}
          type="primary"
          onClick={handleOnFinish}
          disabled={arrSteps[arrSteps.length - 1].stepDisableNext}
        >
          {translate('buttons.confirm')}
        </Button>
      )
    );
  }, [
    arrSteps,
    currentStepNumber,
    finishButtonLoading,
    handleOnFinish,
    translate,
  ]);

  const [flagStart, setFlagStart] = useState(false);

  const startButton = useMemo(() => {
    return (
      <Button
        type="primary"
        onClick={() => {
          setFlagStart(true);
        }}
      >
        {translate('buttons.start')}
      </Button>
    );
  }, [translate]);

  const introCancelButton = useMemo(() => {
    if (modalConfig?.closable === false) {
      return <></>;
    }
    return (
      <Button
        type="default"
        onClick={e => {
          onCancel(e);
        }}
      >
        {translate('buttons.cancel')}
      </Button>
    );
  }, [modalConfig?.closable, onCancel, translate]);

  return (
    <Modal
      style={{ alignItems: 'flex-start' }}
      footer={[
        <div key="0" className="steps-action">
          {!introStep || (introStep && flagStart) ? (
            <>
              {prevButton}
              {nextButton}
              {finishButton}
            </>
          ) : (
            <>
              {introCancelButton}
              {startButton}
            </>
          )}
        </div>,
      ]}
      {...modalConfig}
      bodyStyle={{ ...modalConfig?.bodyStyle, padding: '15px 20px' }}
      title={
        <div style={{ fontSize: '0.875rem' }}>{modalConfig?.title || ''}</div>
      }
      afterClose={() => {
        document.body.style.removeProperty('overflow');
        if (modalConfig?.afterClose) modalConfig?.afterClose();
        setCurrentStepNumber(0);
        setFlagStart(false);
      }}
      open={modalVisible}
      onCancel={onCancel}
    >
      {introStep && !flagStart && <div>{introStep}</div>}
      {(!introStep || (introStep && flagStart)) && (
        <>
          {!hideSteps && (
            <Steps current={currentStepNumber} {...stepsConfig}>
              {arrSteps.map((step: any) => {
                const { stepConfig } = step;
                return (
                  <Step key={stepConfig?.title} title={stepConfig?.title} />
                );
              })}
            </Steps>
          )}
          <div style={{ marginTop: !hideSteps ? '10px' : '0px' }}>
            {arrSteps[currentStepNumber].stepContent}
          </div>
        </>
      )}
    </Modal>
  );
};

export default Wizard;
